import React, { useState } from "react";
import { Button, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import type { RcFile, UploadFile } from "antd/es/upload";

import getDummyRequest from "./helpers/getDummyRequest";

const DOCUMENTS_SUPPORTED_FILES = [
  "application/pdf",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/rtf",
  "text/plain",
];

const LABEL_SELECT = "Select Folder";

const STATUS_DONE = "done";

const HIDDEN_FILE_PREFIX = ".";

type FileLike = {
  name: string;
  type?: string;
};

type OnSuccess = (
  supportedFiles: RcFile[],
  metadatas: UploadFile[],
  unsupportedFileNames: string[],
) => void;

type Props = {
  onSuccess: OnSuccess;
};

const isHiddenFile = ({ name }: FileLike): boolean => name.startsWith(HIDDEN_FILE_PREFIX);

const isFileTypeSupported = ({ name, type }: FileLike): boolean => {
  const isHidden = isHiddenFile({ name });
  const isSupportedType = DOCUMENTS_SUPPORTED_FILES.includes(type);

  const isSupported = !isHidden && isSupportedType;

  return isSupported;
};

const SelectFolderButton = ({ onSuccess }: Props): JSX.Element => {
  const [ files, setFiles ] = useState<RcFile[]>([]);
  const [ metadatas, setMetadatas ] = useState<UploadFile[]>([]);

  const customRequest = getDummyRequest((selectedFile) =>
    setFiles((prevFiles) => [ ...prevFiles, selectedFile ]),
  );

  const finish = (resultMetadatas: UploadFile[]): void => {
    const supportedFiles = files
      .filter(isFileTypeSupported);

    const unsupportedFileNames = files
      .filter((item) => !isHiddenFile(item))
      .filter((item) => !isFileTypeSupported(item))
      .map(({ name: fileName }) => fileName);

    onSuccess(supportedFiles, resultMetadatas, unsupportedFileNames);

    setFiles([]);
    setMetadatas([]);
  };

  const onChange: UploadProps["onChange"] = ({ file: metadata, fileList }) => {
    const { uid, type, name, status } = metadata;

    const isDone = status === STATUS_DONE;
    const isHidden = name.startsWith(HIDDEN_FILE_PREFIX);
    const isSupportedType = DOCUMENTS_SUPPORTED_FILES.includes(type);

    const [ lastFile ] = fileList.slice(-1);
    const isLast = uid === lastFile.uid;

    const shouldSkipFile = !isDone || isHidden || !isSupportedType;

    if (shouldSkipFile) {
      if (!isLast) {
        return;
      }

      finish(metadatas);
      return;
    }

    if (!isLast) {
      setMetadatas((prevMetadatas) => [ ...prevMetadatas, metadata ]);
      return;
    }

    finish([ ...metadatas, metadata ]);
  };

  return (
    <Upload
      directory
      multiple={false}
      onChange={onChange}
      customRequest={customRequest}
      showUploadList={false}
    >
      <Button
        type="dashed"
        icon={<UploadOutlined />}
      >
        {LABEL_SELECT}
      </Button>
    </Upload>
  );
};

export default SelectFolderButton;
